import {
  Component, ElementRef, OnInit, ViewChild, ViewEncapsulation,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import {
  defer, each, isArray, isEmpty, isEqual, isNil, omitBy, set, size,
} from 'lodash';
import { PaginationInstance } from 'ngx-pagination';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import {
  BehaviorSubject, Observable, Subject, firstValueFrom,
} from 'rxjs';
import {
  auditTime, debounceTime, distinctUntilChanged, filter, map, takeUntil,
} from 'rxjs/operators';
import { MediaSearchQuery } from 'src/api/search/media/media-search.query';
import { MediaSearchService } from 'src/api/search/media/media-search.service';
import { AddMediaTagsDialogComponent } from 'src/components/dialogs/add-media-tags-dialog/add-media-tags-dialog.component';
import { ArchiveMediaItemDialogComponent } from 'src/components/dialogs/archive-media-item-dialog/archive-media-item-dialog.component';
import { NgxMasonryComponent } from 'src/components/layout/ngx-masonry/ngx-masonry.component';
import { PermissionProvider } from 'src/providers/permission.provider';
import { DoenkidsSessionProvider } from 'src/providers/session.provider';
import { IMediaSearchRequest } from 'typings/api-search';
import { ISearchMedia } from 'typings/doenkids/doenkids';
import { TranslateService } from 'src/app/utils/translate.service';
import { IUserPermission } from 'typings/api-customer';
import { ISearchEvent } from 'src/components/shared/activity-search-bar/activity-search-bar.component';

_('media_tags.labels.activity_media_tags');
_('media_tags.labels.activity_section_media_tags');
_('media_tags.labels.media_tags');

interface IStoredMediaSearch {
  queryParams: IMediaSearchRequest;
  concat: boolean;
  aggregations: any;
}

@Component({
    selector: 'app-media-tags',
    templateUrl: './media-tags.component.html',
    styleUrls: ['./media-tags.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class MediaTagsComponent implements OnInit {
  @ViewChild(NgxMasonryComponent) masonry: NgxMasonryComponent;

  private stop$ = new Subject();

  private SEARCH_LIMIT = 30;

  public mediaSearchStorageKey = 'mediaSearch';

  public allowedToEdit$: Observable<boolean>;

  private imagesLoaded$ = new Subject();

  public searchResults$: Observable<ISearchMedia[]>;

  public isSearching$: Observable<boolean>;

  public mediaSearchMetadata$: Observable<any>;

  public searchSuggestions$: Observable<any>;

  public aggregations$: Observable<any>;

  public aggregationControl: UntypedFormControl = new UntypedFormControl();

  /** The labels for the aggregationlist */
  public aggregationLabels$: Observable<{ [key: string]: string }>;

  // Observable that will inform us if an aggregation is selected on the lefthand-side
  //
  public containsAggregations$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public hideAggregations$ = new BehaviorSubject<boolean>(false);

  private scrollContainer: HTMLElement;

  private fetchMedia$: Subject<{
    queryParams: IMediaSearchRequest,
    concatenateResults?: boolean,
    fetchAllAndIgnoreSkip?: boolean,
    aggregations?: any,
  }> = new Subject();

  public currentSearchParams: IMediaSearchRequest;

  public currentPage: number;

  /**
   * The configuration object for the pagination of the list.
   */
  public paginationConfig$: Observable<PaginationInstance>;

  public isAdmin$: Observable<boolean>;

  public userWritePermissionsOuIds$: Observable<IUserPermission[]>;

  constructor(
    private dialog: MatDialog,
    private $permissions: PermissionProvider,
    private $session: DoenkidsSessionProvider,
    private elementRef: ElementRef,
    private mediaSearchQuery: MediaSearchQuery,
    private mediaSearchService: MediaSearchService,
    private $translateService: TranslateService,
  ) {
    this.allowedToEdit$ = this.$permissions.hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$.asObservable();
    this.searchResults$ = this.mediaSearchQuery.selectAll();
    this.isSearching$ = this.mediaSearchQuery.selectLoading();
    this.mediaSearchMetadata$ = this.mediaSearchQuery.getMetadata;
    this.searchSuggestions$ = this.mediaSearchQuery.getSuggestions;
    this.aggregations$ = this.mediaSearchQuery.getAggregations.pipe(
      debounceTime(100),
      filter((aggregations) => !isNil(aggregations)),
      map((aggregations) => aggregations),
    );

    this.aggregationLabels$ = this.$translateService
      .onInitialTranslationAndLangOrTranslationChange$
      .pipe(
        takeUntil(this.stop$),
        map((langChange) => ({
          media_tags: langChange.translations[_('media_tags.aggregation_labels.tags')],
          activity_media_tags: langChange.translations[_('media_tags.aggregation_labels.activity_media_tags')],
          activity_section_media_tags: langChange.translations[_('media_tags.aggregation_labels.activity_section_media_tags')],
        })),
      );

    this.paginationConfig$ = this.mediaSearchMetadata$.pipe(
      map((metaData) => {
        const paginationConfig: PaginationInstance = {
          currentPage: this.currentPage,
          itemsPerPage: metaData.limit,
          totalItems: metaData.total,
        };

        return paginationConfig;
      }),
    );

    this.isAdmin$ = this.$session.isAdmin$;
    this.userWritePermissionsOuIds$ = this.$session.userPermissions$.pipe(
      takeUntil(this.stop$),
      map((userPermissions) => userPermissions.filter((userPermission) => userPermission.permission === 'WRITE')),
    );
  }

  async ngOnInit() {
    const organizationUnitId = await firstValueFrom(this.$session.currentOuId$);
    this.currentSearchParams = {
      query: '',
      limit: this.SEARCH_LIMIT,
      skip: 0,
      nodeOnly: false,
      organizationUnitId,
      archived: false,
      filter: {},
      purpose: ['activity', 'template'],
    };

    // Make sure we don't fetch activities unnecessarily
    //
    this.fetchMedia$.pipe(
      filter((request) => !isNil(request)),
      takeUntil(this.stop$),
      distinctUntilChanged(isEqual),
    ).subscribe((request) => {
      this.doFetchMedia(request.queryParams, request.concatenateResults, request.fetchAllAndIgnoreSkip);
    });

    this.$session.getOrganizationUnitSwitch$.pipe(
      takeUntil(this.stop$),
    ).subscribe((ou) => {
      this.currentSearchParams.organizationUnitId = ou.id;
      this.fetchMedia$.next({
        queryParams: this.currentSearchParams,
        concatenateResults: false,
        fetchAllAndIgnoreSkip: true,
        aggregations: this.getAggregationControlValue(),
      });
    });

    await this.storedSearch();

    let containsAggregationsOptions = false;
    each(this.getAggregationControlValue(), (value) => {
      if (isArray(value) && size(value) > 0) {
        containsAggregationsOptions = true;
      }
    });
    this.containsAggregations$.next(containsAggregationsOptions);

    // Listen for updates of the activities
    //
    this.searchResults$.pipe(
      takeUntil(this.stop$),
    ).subscribe(() => {
      if (this.masonry) {
        this.updateMasonryLayout();
      }
    });

    // Update masonay when all images are loaded
    //
    this.imagesLoaded$.pipe(
      takeUntil(this.stop$),
      auditTime(300),
    ).subscribe(() => {
      if (this.masonry) {
        this.updateMasonryLayout();
      }
    });

    // Prepare a observable that is true when the aggragations that are selected have changed
    //
    this.aggregationControl.valueChanges.pipe(
      distinctUntilChanged((a, b) => {
        // Only compare the selected values, because new aggregations can be added
        // during a text search, this should not interfere with this subscription
        //
        const newA = omitBy(a, isEmpty);
        const newB = omitBy(b, isEmpty);
        return isEqual(newA, newB);
      }),
    ).subscribe((newAggregations) => {
      containsAggregationsOptions = false;
      each(newAggregations, (value) => {
        if (isArray(value) && size(value) > 0) {
          containsAggregationsOptions = true;
        }
      });

      this.containsAggregations$.next(containsAggregationsOptions);

      this.fetchMedia$.next({
        queryParams: this.currentSearchParams,
        concatenateResults: false,
        fetchAllAndIgnoreSkip: true,
        aggregations: newAggregations,
      });
    });
  }

  onScroll(ref: ElementRef<any>) {
    this.scrollContainer = ref.nativeElement;
  }

  onScrollEnd(ref: ElementRef<any>) {
    if (isNil(this.scrollContainer)) {
      this.scrollContainer = ref.nativeElement;
    }
    this.loadMoreMedia();
  }

  tabChange(tab: MatTabChangeEvent) {
    if (tab.index === 1) {
      // setTimeout(() => this.updateMasonryLayout(), 0);
    }
  }

  getAggregationControlValue() {
    const aggregations: { [index: string]: [any] } = isNil(this.aggregationControl.value) ? {} : this.aggregationControl.value;

    return aggregations;
  }

  doSearch(searchEvent: ISearchEvent) {
    this.fetchMedia$.next({
      queryParams: {
        ...this.currentSearchParams,
        query: searchEvent?.query ?? '',
      },
      concatenateResults: false,
      fetchAllAndIgnoreSkip: true,
      aggregations: this.getAggregationControlValue(),
    });
  }

  async doFetchMedia(queryParams: IMediaSearchRequest, concatenateResults: boolean, fetchAllAndIgnoreSkip: boolean) {
    if (queryParams.skip === 0) {
      concatenateResults = false;
    }

    const aggregations = this.getAggregationControlValue();
    const organizationUnitId = await firstValueFrom(this.$session.currentOuId$);

    this.currentSearchParams = {
      ...queryParams,
      organizationUnitId,
      filter: aggregations,
    };

    // When restoring a search from locale storage
    // we want to do a full search and igore the skip parameter
    //
    if (fetchAllAndIgnoreSkip === true) {
      this.currentSearchParams.limit += this.currentSearchParams.skip;
      this.currentSearchParams.skip = 0;
    }

    // Store the search params to use later.
    //
    const storedSearch: IStoredMediaSearch = {
      queryParams: this.currentSearchParams, concat: concatenateResults, aggregations,
    };

    // eslint-disable-next-line no-undef
    localStorage.setItem(this.mediaSearchStorageKey, JSON.stringify(storedSearch));

    this.mediaSearchService.fetch(this.currentSearchParams, concatenateResults);
  }

  private async storedSearch() {
    // eslint-disable-next-line no-undef
    const searchValuesJSON = localStorage.getItem(this.mediaSearchStorageKey);

    if (isNil(searchValuesJSON)) {
      this.fetchMedia$.next({
        queryParams: this.currentSearchParams,
        concatenateResults: false,
        fetchAllAndIgnoreSkip: true,
        aggregations: this.getAggregationControlValue(),
      });
      return;
    }

    const storedSearch: IStoredMediaSearch = JSON.parse(searchValuesJSON);
    const currentOUId = await firstValueFrom(this.$session.currentOuId$);

    // when the stored search if for another OU stop with setup as it is not a valid search for this OU
    //
    if (storedSearch.queryParams.organizationUnitId === currentOUId) {
      // get the aggregation settings from the stored search
      //
      this.aggregationControl.setValue(storedSearch.aggregations);

      // eslint-disable-next-line prefer-destructuring
      const queryParams: IMediaSearchRequest = storedSearch.queryParams;

      this.currentSearchParams = queryParams;
    }

    // override search limit from stored search to reflect new one
    //
    this.currentSearchParams.limit = this.SEARCH_LIMIT;

    // lets not set the whole result set but just the first limited result set
    //
    this.currentSearchParams.skip = 0;

    this.fetchMedia$.next({
      queryParams: this.currentSearchParams,
      concatenateResults: false,
      fetchAllAndIgnoreSkip: true,
      aggregations: this.getAggregationControlValue(),
    });
  }

  // Update the masonry layout
  //
  updateMasonryLayout() {
    if (this.masonry) {
      this.masonry.reloadItems();
      this.masonry.layout();
    }
  }

  imageLoaded() {
    this.imagesLoaded$.next(undefined);
  }

  async deleteMediaItem(mediaItem: ISearchMedia) {
    const dialogRef = this.dialog.open(ArchiveMediaItemDialogComponent, {
      width: '100%',
      maxWidth: '80%',
      minWidth: '320px',
      data: { item: mediaItem },
    });

    const isRemoved = await firstValueFrom(dialogRef.afterClosed());
    if (isRemoved) {
      const element = this.elementRef.nativeElement.querySelector(`#media${mediaItem.uuid}`);
      if (element) {
        element.remove();
        defer(() => {
          this.updateMasonryLayout();
        });
      }
    }
  }

  async openTagDialog(mediaItem: ISearchMedia) {
    const dialogRef = this.dialog.open(AddMediaTagsDialogComponent, {
      width: '600px',
      minWidth: '400px',
      data: {
        mediaItem,
      },
    });

    const result = await firstValueFrom(dialogRef.afterClosed());

    if (result?.action === 'confirm') {
      this.doFetchMedia(this.currentSearchParams, false, true);
    }
  }

  public removeFilter($event: { name: string, value: string }) {
    const { name, value } = $event;
    const currentSelection: { [name: string]: string[] } = this.aggregationControl.value;
    if (currentSelection && currentSelection[name] && isArray(currentSelection[name]) && currentSelection[name].includes(value)) {
      currentSelection[name] = currentSelection[name].filter((label) => label !== value);
      this.aggregationControl.setValue(currentSelection);
    }
  }

  public removeAllFilters() {
    const currentSelection: { [name: string]: string[] } = this.aggregationControl.value;
    if (currentSelection) {
      each(currentSelection, (value, key) => {
        set(currentSelection, key, []);
      });
      this.aggregationControl.setValue(currentSelection);
    }
  }

  public async loadMoreMedia() {
    const offset = this.currentSearchParams.skip + this.currentSearchParams.limit;

    const { total } = (await firstValueFrom(this.mediaSearchQuery.getMetadata));

    // Check if there are more to fetch
    //
    if (offset < total) {
      this.fetchMedia$.next({
        queryParams: {
          ...this.currentSearchParams,
          skip: offset,
        },
        concatenateResults: true,
        fetchAllAndIgnoreSkip: false,
        aggregations: this.getAggregationControlValue(),
      });
    }
  }
}
